import { useEffect, useState } from 'react';
import { Box, Button, Divider, Menu, MenuItem, MenuList, Paper, Popper, Tooltip } from '@mui/material';
import NextLink from 'next/link';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import CheckIcon from '@mui/icons-material/Check';
import SupportPanel from 'components/support/SupportPanel';
import notify from 'lib/notify';
import { readFile, sendApiRequest } from 'lib/api/public';
import { useTheme as useThemeContext } from 'lib/ThemeContext';
import { User } from 'components/types';

const workspaceMenuPaperSx = {
  mt: 0.5,
  minWidth: 176,
  p: 0.25,
  overflow: 'visible',
  backgroundImage: 'none',
  border: (theme: { palette: { divider: string } }) => `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
};

const workspaceMenuItemSx = {
  minHeight: 26,
  height: 26,
  px: 0.75,
  py: 0,
  gap: 0.75,
  borderRadius: '4px',
  fontSize: 12,
  fontWeight: 400,
  color: 'text.primary',
};

const WorkspaceMenuIcon = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 14,
      height: 14,
      color: 'text.secondary',
      flexShrink: 0,
      '& .MuiSvgIcon-root': { fontSize: 14 },
    }}
  >
    {children}
  </Box>
);

const WorkspaceMenu = ({ user, drawerCollapsed }: { user: User | null; drawerCollapsed: boolean }) => {
  const { isDarkMode, setTheme } = useThemeContext();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [themeAnchorEl, setThemeAnchorEl] = useState<HTMLElement | null>(null);
  const [supportOpen, setSupportOpen] = useState(false);
  const themeLabel = isDarkMode ? 'Dark' : 'Light';
  const canInviteUser = user?.role === 'superAdmin';
  const menuOpen = Boolean(anchorEl);

  const closeMenus = () => {
    setAnchorEl(null);
    setThemeAnchorEl(null);
  };

  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }

    const onMouseDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      if (target.closest('#workspace-menu, #workspace-theme-menu, #workspace-menu-button')) {
        return;
      }
      closeMenus();
    };

    document.addEventListener('mousedown', onMouseDown, true);
    return () => document.removeEventListener('mousedown', onMouseDown, true);
  }, [menuOpen]);

  const applyTheme = (nextTheme: 'light' | 'dark') => {
    if ((nextTheme === 'dark') === isDarkMode) {
      return;
    }

    setTheme(nextTheme);

    if (!user?.firstName || !user.lastName || !user.email) {
      return;
    }

    void sendApiRequest({
      path: '/users/me',
      method: 'POST',
      data: {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        theme: nextTheme,
      },
    }).catch(err => notify(err));
  };

  return (
    <>
      <Tooltip title={drawerCollapsed ? 'Harbor' : ''} placement="right">
        <Button
          color="secondary"
          id="workspace-menu-button"
          aria-controls={menuOpen ? 'workspace-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={menuOpen ? 'true' : undefined}
          aria-label="Harbor workspace menu"
          onClick={event => {
            if (menuOpen) {
              closeMenus();
              return;
            }
            setAnchorEl(event.currentTarget);
          }}
          sx={{
            flex: drawerCollapsed ? '0 0 auto' : '0 1 auto',
            width: drawerCollapsed ? undefined : 'fit-content',
            maxWidth: '100%',
            minWidth: 0,
            justifyContent: drawerCollapsed ? 'center' : 'flex-start',
            textAlign: 'left',
            textTransform: 'none',
            color: 'secondary.contrastText',
            fontWeight: 500,
            px: 0.5,
            py: 0.5,
            borderRadius: 1,
            backgroundColor: menuOpen ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: drawerCollapsed ? 0 : 1,
              minWidth: 0,
              maxWidth: '100%',
            }}
          >
            <Box
              component="img"
              src={user?.account?.logo ? readFile(user.account.logo) : '/logo.png'}
              alt={user?.account?.logo ? user.account.name || '' : 'Harbor'}
              sx={{
                width: 22,
                height: 22,
                objectFit: 'cover',
                objectPosition: user?.account?.logo ? 'center' : 'left center',
                borderRadius: '50%',
                flexShrink: 0,
              }}
            />
            {!drawerCollapsed && (
              <Box
                component="span"
                sx={{
                  whiteSpace: 'nowrap',
                  fontSize: 13,
                  fontWeight: 500,
                  lineHeight: 1.4,
                }}
              >
                {user?.account?.name || 'Harbor'}
              </Box>
            )}
            {!drawerCollapsed && (
              <KeyboardArrowDownIcon
                sx={{
                  fontSize: 16,
                  flexShrink: 0,
                  opacity: 0.7,
                }}
              />
            )}
          </Box>
        </Button>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            closeMenus();
          }
        }}
        disableEnforceFocus
        disableAutoFocusItem
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          list: {
            id: 'workspace-menu',
            'aria-labelledby': 'workspace-menu-button',
            sx: { py: 0 },
          },
          paper: { sx: workspaceMenuPaperSx },
        }}
      >
        <MenuItem
          aria-haspopup="true"
          aria-expanded={themeAnchorEl ? 'true' : undefined}
          aria-controls={themeAnchorEl ? 'workspace-theme-menu' : undefined}
          onClick={event => setThemeAnchorEl(event.currentTarget)}
          sx={{
            ...workspaceMenuItemSx,
            backgroundColor: themeAnchorEl ? 'action.selected' : undefined,
          }}
        >
          <WorkspaceMenuIcon>
            <DarkModeOutlinedIcon />
          </WorkspaceMenuIcon>
          <Box component="span" sx={{ lineHeight: 1 }}>
            Theme
            <Box component="span" sx={{ color: 'text.secondary' }}>
              {' '}
              · {themeLabel}
            </Box>
          </Box>
          <ChevronRightIcon sx={{ fontSize: 14, color: 'text.secondary', ml: 'auto' }} />
        </MenuItem>

        <MenuItem
          onClick={() => {
            setThemeAnchorEl(null);
            setSupportOpen(true);
          }}
          sx={workspaceMenuItemSx}
        >
          <WorkspaceMenuIcon>
            <ChatBubbleOutlineIcon />
          </WorkspaceMenuIcon>
          Support
        </MenuItem>

        <MenuItem
          component="a"
          href="https://status.findharbor.com"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setThemeAnchorEl(null)}
          sx={workspaceMenuItemSx}
        >
          <WorkspaceMenuIcon>
            <MonitorHeartOutlinedIcon />
          </WorkspaceMenuIcon>
          Status
        </MenuItem>

        {canInviteUser ? <Divider sx={{ my: '2px !important' }} /> : null}

        {canInviteUser ? (
          <MenuItem component={NextLink} href="/settings/team" sx={workspaceMenuItemSx}>
            <WorkspaceMenuIcon>
              <PersonAddAltOutlinedIcon />
            </WorkspaceMenuIcon>
            Invite team member
          </MenuItem>
        ) : null}
      </Menu>

      <Popper
        open={Boolean(themeAnchorEl)}
        anchorEl={themeAnchorEl}
        placement="right-start"
        modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
        sx={{ zIndex: theme => theme.zIndex.modal + 1 }}
      >
        <Paper
          sx={{
            ...workspaceMenuPaperSx,
            mt: 0,
            minWidth: 112,
            pointerEvents: 'auto',
          }}
        >
          <MenuList
            id="workspace-theme-menu"
            autoFocusItem={false}
            sx={{ py: 0 }}
            onKeyDown={event => {
              if (event.key === 'Escape') {
                closeMenus();
              }
            }}
          >
            <MenuItem onClick={() => applyTheme('light')} sx={workspaceMenuItemSx}>
              Light
              {!isDarkMode ? <CheckIcon sx={{ fontSize: 14, ml: 'auto' }} /> : null}
            </MenuItem>
            <MenuItem onClick={() => applyTheme('dark')} sx={workspaceMenuItemSx}>
              Dark
              {isDarkMode ? <CheckIcon sx={{ fontSize: 14, ml: 'auto' }} /> : null}
            </MenuItem>
          </MenuList>
        </Paper>
      </Popper>

      <SupportPanel open={supportOpen} onClose={() => setSupportOpen(false)} />
    </>
  );
};

export default WorkspaceMenu;
